/**
 * Application Entry Point
 * Bootstraps React application with required dependencies and configuration
 * 
 * Initialization order (CRITICAL):
 * 1. LaunchDarkly mock (for REX components)
 * 2. Runtime settings loading
 * 3. MFE remote entries (Host mode only)
 * 4. Datadog RUM (if enabled)
 * 5. Global error handler
 * 6. React app render
 */

import ReactDOM from 'react-dom/client';
import App from './App';
import { GlobalErrorHandler } from './shared/global-error-handler';
import { httpClient } from './shared/http-client';
import { getAccessToken } from './shared/auth-session';
import './styles/tailwind.css';
import { initLaunchDarklyMock } from './launchdarkly-mock';
import { loadSettings } from './shared/settings-loader';
import './coverage_reporter';

/**
 * STEP 1: Initialize LaunchDarkly mock
 * CRITICAL: Must run BEFORE any REX components initialize
 * Provides feature flag values without requiring LaunchDarkly connection
 */
initLaunchDarklyMock();

/**
 * Application Bootstrap
 * Async initialization sequence for runtime configuration and services
 */
(async () => {
  /**
   * STEP 4: Load runtime settings (no-op; auth config removed)
   */
  await loadSettings();

  httpClient.setTokenProvider(async () => getAccessToken());

  /**
   * STEP 7: Initialize global error handler
   * Sets up window.onerror and unhandledrejection handlers
   */
  GlobalErrorHandler.init();

  /**
   * STEP 8: Render React application
   * Creates root and mounts App component
   * 
   * NOTE: React StrictMode is disabled in production
   * StrictMode intentionally double-invokes mount/unmount in dev mode,
   * which can cause issues with Angular-based custom elements (e.g., REX components)
   */
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );

  root.render(<App />);
})();
